import { StatType } from "../enums/index.js";
import StatisticalHelper from "../helpers/StatisticalHelper.js";

/**
 * Created by Adrian Kostarek on 2016-06-24.
 * @version 2.8 @2016-08-04
 */
class IterableDataProcessor {
	constructor(enumValueProvider, converter) {
		this.rawData = null; //stores raw data obtained from server and processed by preProcess function
		this.processedData = []; //stores rawData processed by preProcesse function
		this.filteredData = []; //contains data processed by preProcess function and filtered by filterData()
		this.sortedData = []; //contains data processed by preProcess function, filtered by filterData() and ordered by sortData()
		this.data = []; //contains data processed by preProcess function, filtered by filterData(), ordered by sortData() and grouped by groupData()
		this.filters = []; //stores filters definition, see addFilter for more details
		this.predefinedFilters = []; //stores predefined filters definition, those will not be deleted via clearFilters
		this.filtersRelation = '&&'; //defines type of logic operator applied between multiple filters
		this.filtersStorage = {}; //storage for filters
		this.filtersBase = []; //base path from within filtering will start
		this.orderBy = []; //defines data order rules, array containing following object structure: field - parametr that will be used to determine order in data set (or array with path), order - ASC or DESC
		this.orderByBase = []; ////base path from within sorting will start
		this.groupBy = []; //defines grouping aggrgators. Each array value will be used in separate, recursive iteration. If array will be specified then all items will be used as a aggregator
		this.groupBase = []; ////base path from within grouping will start
		this.unitsPaths = []; //array with paths to units that should be converted. Each array element should be defined as an array with each object key as an value. Units will be replaced after preProcessing
		this.datePaths = []; //array with paths to date values that should be converted. Each array element should be defined as an array with each object key as an value. Dates will be replaced after preProcessing
		this.enumsPaths = []; //array with paths to enums that should be converted. Each array element should be defined as an array with each object key as an value. Single values will be replaced after preProcessing
		this.enumsMapping = {}; //object with configuration for enumeration, parameter name should be exact like parameterName in enumsPaths, it should contain string for single matching or object with following attributes: enumDef: name of enumeration definition; defaultKey: default value; specificPropertyOnly - array with keys which should be returned from enum
		this.statsDataPaths = []; //array with paths to values that will be used during stats calculation. Each array element should be defined as an array with each object key as an value. Last array element can be defined as an array with multiple parameters
		this.calculatedStats; //contains set of statistical data, useful when output is not defined as an object
		this.uniqueProperties = []; //defines array of parameters which will be used to unique values filtering
		this.uniquePropertiesPath = []; //defines default destination path of parameters used to determine uniqueness
		this.currentConf = null; //stores current configuration of the model instance
		this.enumDefs = {}; //contains collection of enum definitions (object with key as a property name and desired value)
		this.converter = converter || null; // units converter
		this.enumValueProvider = enumValueProvider || null; // enum value provider
		this.helpers = {};
	}
    /**
    * configures model for specific view
    * @param string confName - optional, name of the configuration set
    * @return void
    */
	configure(confName) {
		this.reset();
		this.currentConf = confName;
	}
    /**
    * configures model for specific view
    * @param string confName - optional, name of the configuration set
    * @return void
    */
	setRawData(rawData) {
		if (rawData instanceof Array || rawData instanceof Object) {
			this.rawData = rawData;
		}
		throw new Error('rawData must be an instance of Array or Object');
	}
    /**
    * reset model state
    * @return void
    */
	reset(include, exclude) {
		let processed = [];
		if (typeof (include) == 'undefined') {
			include = [
				'filters', 'predefinedFilters', 'filtersRelation', 'filtersStorage', 'filtersBase',
				'orderBy', 'orderByBase', 'groupBy', 'groupBase',
				'unitsPaths', 'datePaths', 'enumsPaths', 'enumsMapping', 'statsDataPaths', 'uniqueProperties', 'uniquePropertiesPath'
			];
		}
		if (typeof (exclude) == 'undefined') {
			exclude = [];
		}
		for (let i in include) {
			let curParam = include[i];
			if (exclude.indexOf(curParam) > -1) {
				continue;
			}
			processed.push(curParam);
			switch (curParam) {
				case 'filtersRelation':
					this[curParam] = '&&';
					break;
				case 'filtersStorage':
					this[curParam] = {};
					break;
				case 'enumsMapping':
					this[curParam] = {};
					break;
				default:
					this[curParam] = [];
			}
		}
		return processed;
	}
    /**
    * function used to pre-preocess rawData
    * @return array - processed rawData
    */
	preProcess() {
		this.processedData = this.cloneObject(this.rawData);
		return this.processedData;
	}
    /**
    * post processing of data
    * @return array - processed data
    */
	postProcess() {
		return this.data;
	}
    /**
    * clone input object and get rid of refference
    * @param object input - input object
    * @return object - clone of input object without reference
    */
	cloneObject(input) {
		if (input === null || typeof input !== 'object') {
			return input;
		}
		let temp = input.constructor(); // give temp the original obj's constructor
		for (let key in input) {
			if (typeof (input[key]) == 'object' && input[key] !== null) {
				temp[key] = this.cloneObject(input[key]);
			}
			else {
				temp[key] = input[key];
			}
		}
		return temp;
	}
    /**
    * clone input object into array
    * @param object object - input object
    * @return array - array converted from input object without attributes
    */
	objectToArray(object) {
		if (object === null || typeof object !== 'object' || object instanceof Array) {
			return object;
		}
		let array = [];
		for (let i in object) {
			array.push(object[i]);
		}
		return array;
	}
    /**
    * get object contents defined by path
    * @param object input - input object
    * @param array path - array with specifies consecutive object keys
    * @return object - object content defined by path
    */
	getObjectContent(input, path) {
		return IterableDataProcessor.getObjectContent(input, path);
	}

	/**
    * get object contents defined by path
    * @param object input - input object
    * @param array path - array with specifies consecutive object keys
    * @return object - object content defined by path
    */
   static getObjectContent(input, path) {
		let output;
		output = input;
		for (let l in path) {
			let curLevel = path[l];
			if (typeof (curLevel) !== 'object' && output !== null && output.hasOwnProperty(curLevel)) {
				output = output[curLevel];
			}
			else {
				return;
			}
		}
		return output;
	}

	/**
    * sets object contents defined by path
    * @param object input - input object
    * @param array path - array with specifies consecutive object keys
	* @param any newValue - new value to be set
    * @return bool - true when succeeded
    */
	static setObjectContent(object, path, newValue) {
		let hasSet = false;
		if (path.length === 0){
			return hasSet;
		}

		let nestedObject = object;
		const iterateTo = path.length - 1;
		const destPropertyName = path[iterateTo];

		for (let i = 0; i < iterateTo; i++) {
			let curLevel = path[i];
			if (typeof (curLevel) !== 'object' && nestedObject !== null && nestedObject.hasOwnProperty(curLevel)) {
				nestedObject = nestedObject[curLevel];
			}
			else {
				return hasSet;
			}
		}

		if (typeof(nestedObject) === 'object' && nestedObject !== null) {
			nestedObject[destPropertyName] = newValue;
			hasSet = true;
		}

		return hasSet;
	}

	/**
    * sets object contents defined by path
    * @param object input - input object
    * @param array path - array with specifies consecutive object keys
	* @param any newValue - new value to be set
    * @return bool - true when succeeded
    */
	setObjectContent(object, path, newValue) {
		return IterableDataProcessor.setObjectContent(object, path, newValue);
	}

    /**
    * defines new data filters, relation between multiple filters specifies filtersRelation parameter ( &&, || )
    * @param mixed parameter - parameter (string) from data which will verified or (array) with path definition if filtered parameter is nested
    * @param string operator - logic operator, possible values and values requirements:
    *	'=='		- parameter is equal to value, no type checking
    *	'==='		- parameter is equal to value, strict
    *	'!='		- parameter is not equal to value, no type checking
    *	'!=='		- parameter is not equal to value, strict
    *	'>'			- parameter is greater than value
    *	'>='		- parameter is greater than or equal to value
    *	'<'			- parameter is less than value
    *	'<='		- parameter is less than or equal to value
    *	'IN'		- parameter is euqal to one of the values in value array
    *	'NOTIN'		- parameter is not euqal to any of the values in value array
    *	'BETWEEN'	- parameter is greater than or equal to first element in value array and less than or equal to second value array element
    *	'SLICE'		- index of filtered element is greater than or equal to first element in value array and less than or equal to second value array element
    *	'NUMERIC'	- parameter is numeric
    *	'hasProp' 	- parameter is an object and has own property value
    *	'hasNoProp'	- parameter is an object and has no own property value
    *	'hasValue'	- parameter is an array and contain value
    *	'hasNoValue'- parameter is an array and doesn't contain value
    *	'regExp'	- parameter is matching regular expression passed in value, see RegExp.prototype.test()
    * @param mixed value - value that will be compared with parameter value, if function will be passed then boolean value returned will be used for filtering
    * @param predefined bool - defines if filter should be predefined (will not be cleared via clearFilters method)
    * @param storageID string - optional, filter identficator which will be used in filtersStorage
    * @return int - filter index
    */
	addFilter(parameter, operator, value, predefined, storageID) {
		if (typeof (parameter) == 'undefined') {
			throw new Error('addFilter: parameter must be defined');
		}
		if (typeof (operator) == 'undefined') {
			operator = '==';
		}
		if (typeof (predefined) == 'undefined') {
			predefined = false;
		}
		if (typeof (storageID) == 'undefined') {
			storageID = false;
		}
		if (predefined) {
			return (this.predefinedFilters.push({ parameter: parameter, operator: operator, value: value }) - 1);
		}
		let filterIndex = (this.filters.push({ parameter: parameter, operator: operator, value: value }) - 1);
		if (storageID) {
			this.filtersStorage[storageID] = filterIndex;
		}
		return filterIndex;
	}
    /**
    * removes specific filter from storage
    * @param string id - filter identyficator from filtersStorage
    * @return bool
    */
	removeFromFiltersStorage(id) {
		if (this.filtersStorage.hasOwnProperty(id)) {
			this.clearFilters(this.filtersStorage[id]);
			return delete this.filtersStorage[id];
		}
		return false;
	}
    /**
    * resets filters (default - all)
    * @param int index - optional, if passed filter with specific index (returned from addFilter) will be removed
    * @return bool
    */
	clearFilters(index) {
		if (typeof (index) == 'undefined') {
			this.filtersStorage = {};
			this.filters = [];
			return true;
		}
		else if (typeof (this.filters[index]) != 'undefined') {
			delete this.filters[index];
			for (let i in this.filtersStorage) {
				if (this.filtersStorage[i] == index) {
					delete this.filtersStorage[i];
				}
			}
			return true;
		}
		return false;
	}
    /**
    * verify single item against filter
    * @param object filter - should contain paramter, value and operator, see addFilter for detailed information
    * @param object item - item that will be verified
    * @return bool
    */
	filterItem(filter, item, index) {
		let valueToFilter;
		let paramPath = filter.parameter;

		if (typeof (paramPath) === 'string') {
			paramPath = paramPath.length > 0 ? [paramPath] : [];
		}

		if (typeof (paramPath) !== 'object' || paramPath === null){
			throw new Error('unexpected type of path parameter');
		}

		if (item === null || typeof item === 'undefined'){
			return false;
		}

		valueToFilter = this.getObjectContent(item, paramPath);

		//external filtering function
		if (typeof (filter.value) === 'function') {
			return filter.value(valueToFilter);
		}

		switch (filter.operator) {
			case 'undefined': return (typeof (valueToFilter) === 'undefined');
			case 'defined': return !(typeof (valueToFilter) === 'undefined');
			case 'empty': return typeof (valueToFilter) === 'undefined' || valueToFilter === null || valueToFilter === '' || valueToFilter === 0;;
			case 'notEmpty': return typeof (valueToFilter) !== 'undefined' && valueToFilter !== null && valueToFilter !== '' && valueToFilter !== 0;
			case '==': return valueToFilter == filter.value;
			case '===': return valueToFilter === filter.value;
			case '!=': return valueToFilter != filter.value;
			case '!==': return valueToFilter !== filter.value;
			case '>': return valueToFilter > filter.value;
			case '>=': return valueToFilter >= filter.value;
			case '<': return valueToFilter < filter.value;
			case '<=': return valueToFilter <= filter.value;
			case 'CONTAINS': return valueToFilter.indexOf(filter.value) > -1;
			case 'NOTCONTAINS': return valueToFilter.indexOf(filter.value) === -1;
			case 'STARTS': return valueToFilter.indexOf(filter.value) === 0;
			case 'IN': return filter.value.indexOf(valueToFilter) > -1;
			case 'NOTIN': return filter.value.indexOf(valueToFilter) === -1;
			case 'BETWEEN': return valueToFilter >= filter.value[0] && valueToFilter <= filter.value[1];
			case 'SLICE': return index >= filter.value[0] && index <= filter.value[1];
			case 'NUMERIC': return !isNaN(parseFloat(valueToFilter)) && isFinite(valueToFilter);
			case 'LENGTH': return typeof (valueToFilter) !== 'undefined' && valueToFilter.hasOwnProperty('length') && valueToFilter.length === filter.value;
			case 'hasProp': return typeof (valueToFilter) === 'object' && valueToFilter.hasOwnProperty(filter.value);
			case 'hasNoProp': return typeof (valueToFilter) === 'object' && !valueToFilter.hasOwnProperty(filter.value);
			case 'hasValue': return typeof (valueToFilter) !== 'undefined' && valueToFilter.hasOwnProperty('length') && valueToFilter.indexOf(filter.value) > -1;
			case 'hasNoValue': return typeof (valueToFilter) !== 'undefined' && valueToFilter.hasOwnProperty('length') && valueToFilter.indexOf(filter.value) == -1;
			case 'regExp': return filter.value.test(valueToFilter.toString());
			default: throw new Error('filterItem: Unsupported operator: ' + filter.operator);
		}

		return false;
	}
    /**
    * filter raw data stored in array
    * @param array data - optional (default rawData), data set that will be filtered, if passed output will be returned without overwriting the filteredData parameter
    * @param array filters - optional (default filters), set of filters, array should contain objects with following attributes: parameter - parameter which will be filtered, value - string which determines type of verification, value - value which will be compared. See addFilter for more details
    * @param string filtersRelation - optional (default filtersRelation), defines the logic operator between all filters. Possible values: AND OR && ||
    * @param bool indexesOnly - optional (default false), if set to true only array with indexes of filtered elements will be returned. dilteredData set will not be affected
    * @return array - array with filtered parameters (also stored in filteredData when no data attribute is passed)
    */
	filterData(data, filters, filtersRelation, filtersBase, indexesOnly) {
		let curItem, curFilter, filtered = [], filteredData, baseData, customDataSet = true, isArray = false;
		if (typeof (filtersBase) === 'undefined') {
			filtersBase = this.filtersBase;
		}
		if (typeof (data) === 'undefined') {
			data = this.cloneObject(this.processedData);
			customDataSet = false;
		}
		if (typeof (filters) === 'undefined') {
			filters = this.cloneObject(this.predefinedFilters);
			for (let l in this.filters) {
				filters.push(this.filters[l]);
			}
		}
		if (typeof (filtersRelation) === 'undefined') {
			filtersRelation = this.filtersRelation;
		}
		if (typeof (indexesOnly) === 'undefined') {
			indexesOnly = false;
		}
		if (!filters.length) {
			filteredData = this.cloneObject(data);
		}
		else {
			const needDataCopy = (filtersRelation === '&&' || filtersRelation === 'AND') && filters.length > 1;
			baseData = this.getObjectContent(data, filtersBase);
			if (needDataCopy) {
				for (let k in baseData) {
					filtered.push(k);
				}
			}
			if (indexesOnly || baseData.hasOwnProperty('length')) {
				isArray = true;
				filteredData = [];
			}
			else {
				filteredData = {};
			}
			for (let j = 0; j < filters.length; j++) {
				curFilter = filters[j];
				if (typeof (curFilter) === 'undefined') {
					continue;
				}
				if (needDataCopy) {
					let m = 0;
					for (let i = 0; i < filtered.length; i++) {
						if (typeof (filtered[i]) === 'undefined') {
							//value was already exluded by one of the filters
							continue;
						}
						curItem = baseData[filtered[i]];
						if (this.filterItem(curFilter, curItem, m) === false) {
							delete filtered[i];
						}
						m++;
					}
				}
				else {
					let m = 0;
					for (let i in baseData) {
						if (filters.length > 1 && filtered.indexOf(i) > -1) {
							//value already passed one filter
							continue;
						}
						curItem = baseData[i];
						if (this.filterItem(curFilter, curItem, m) !== false) {
							filtered.push(i);
						}
						m++;
					}
				}
			}
			for (let k = 0; k < filtered.length; k++) {
				if (typeof (filtered[k]) != 'undefined') {
					if (indexesOnly) {
						filteredData.push(filtered[k]);
					}
					else if (isArray) {
						filteredData.push(baseData[filtered[k]]);
					}
					else {
						filteredData[filtered[k]] = baseData[filtered[k]];
					}
				}
			}
			//here come the magic - replace only the filter base
			if (!indexesOnly && filtersBase.length) {
				filteredData = this.objectProcessingRecursive(data, filtersBase, 0, function (parameterValue, parameterName, parent) {
					return filteredData;
				});
			}
		}
		if (customDataSet || indexesOnly) {
			return filteredData;
		}
		this.filteredData = filteredData;
		return this.filteredData;
	}
    /**
    * Sort data stored in array. Order can be used efficiently only when data set (pointed by orderByBase) is an array
    * @param array data - optional (default filteredData), data set that will be sorted, if passed output will be returned without overwriting the sortedData parameter
    * @param array orderBy - optional (default orderBy), array containing following object structure: field - parametr that will be used to determine order in data set (or array with path), order - ASC or DESC
    * @return array - array with filtered parameters (also stored in sortedData when no data attribute is passed)
    */
	sortData(data, orderBy, orderByBase) {
		let orderByCallback, j, orderByKey, customDataSet = true, sortedData, baseData;
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.filteredData);
			customDataSet = false;
		}
		if (typeof (orderBy) == 'undefined') {
			orderBy = this.orderBy;
		}
		if (typeof (orderByBase) == 'undefined') {
			orderByBase = this.orderByBase;
		}
		if (typeof (orderBy) == 'string') {
			orderBy = [orderBy];
		}
        /**
        * sorting callback
        * @param mixed a - object or value a
        * @param mixed b - object or value b
        * @return 1, 0, -1 - a is bigger, a is equal to b, a is smaller
        */
		function sortDataCallback(a, b) {
			let i, j, output = 0, curOrderBy, field;
			for (i in orderBy) {
				output = 0;
				curOrderBy = orderBy[i];
				if (typeof (a) != 'object' && typeof (b) != 'object') {
					//regular values, compare them
					output = a - b;
				}
				else if (typeof (a) != 'function' && typeof (b) != 'function') {
					if (typeof curOrderBy.field == 'object') {
						for (j = 0; j < curOrderBy.field.length; j++) {
							field = curOrderBy.field[j];
							if (j < (curOrderBy.field.length - 1)) {
								if (a.hasOwnProperty(field) && b.hasOwnProperty(field)) {
									a = a[field];
									b = b[field];
								}
								else {
									break;
								}
							}
						}
					}
					else {
						field = curOrderBy.field;
					}
					//verify field and order by it...
					if (a.hasOwnProperty(field) && b.hasOwnProperty(field)
						&& typeof (a) == 'object' && typeof (b) == 'object') {
						output = a[field] - b[field];
					}
				}
				if (output === 0) {
					//go to next defined orderBy
					continue;
				}
				if (curOrderBy.order === 'DESC') {
					output *= -1;
				}
			}
			return output;
		}
		if (orderBy.length) {
			for (j in orderBy) {
				if (typeof (orderBy[j]) == 'string') {
					orderBy[j] = { field: orderBy[j], order: 'ASC' };
				}
				orderByKey += (j == 0 ? '' : '_') + orderBy[j]['field'];
			}
			baseData = this.getObjectContent(data, orderByBase);
			if (typeof (this['order_' + orderByKey]) == 'function') {
				orderByCallback = this['order_' + orderByKey];
			}
			else {
				orderByCallback = sortDataCallback;
			}
			baseData.sort(orderByCallback);
			//here come the magic - replace only the order base
			sortedData = this.objectProcessingRecursive(data, orderByBase, 0, function (parameterValue, parameterName, parent) {
				return baseData;
			});
		}
		else {
			sortedData = this.cloneObject(data);
		}
		if (customDataSet) {
			return sortedData;
		}
		this.sortedData = sortedData;
		return this.sortedData;
	}
    /**
    * converts units to desired system
    * @param array data - optional ( default processedData ), data set which will be processed, if passed then output will be returned without overwriting the processedData parameter.
    * @param array unitsPaths - array of units path definition. Each nasted parameter from the top of single data object needs to be included as separate array element. Required units should be passed in separate array or object.
    * @return array - array with converted units for defined parameters (also stored in processedData when no data attribute is passed). Each unit will be replaced with following object: { raw: rawValue, value: convertedValue, type: unitType }
    */
	unitsProcessing(data, unitsPaths) {
		let j, customDataSet = true, processed = [];
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.processedData);
			customDataSet = false;
		}
		if (typeof (unitsPaths) == 'undefined') {
			unitsPaths = this.unitsPaths;
		}
		processed = data;
		for (j in unitsPaths) {
			processed = this.objectProcessingRecursive(processed, unitsPaths[j], 0, this.unitsProcessingRecursive.bind(this));
		}
		if (customDataSet) {
			return processed;
		}
		this.processedData = processed;
		return this.processedData;
	}
    /**
    * converts enums to desired format
    * @param array data - optional ( default processedData ), data set which will be processed, if passed then output will be returned without overwriting the processedData parameter.
    * @param array enumsPaths - array of enums path definition. Each nasted parameter from the top of single data object needs to be included as separate array element. Required parameters to enumerate should be passed in separate array or object.
    * @return array - array with converted enums for defined parameters (also stored in processedData when no data attribute is passed). Each enum will be replaced with following object: { raw: rawValue, value: enumeratedValue } Other keys may be attached if enumsDef says so
    */
	enumsProcessing(data, enumsPaths) {
		let j, customDataSet = true, processed = [];
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.processedData);
			customDataSet = false;
		}
		if (typeof (enumsPaths) == 'undefined') {
			enumsPaths = this.enumsPaths;
		}
		processed = data;
		for (j in enumsPaths) {
			processed = this.objectProcessingRecursive(processed, enumsPaths[j], 0, this.enumsProcessingRecursive.bind(this));
		}
		if (customDataSet) {
			return processed;
		}
		this.processedData = processed;
		return this.processedData;
	}
    /**
    * converts units to desired system
    * @param array data - optional ( default processedData ), data set which will be processed, if passed then output will be returned without overwriting the processedData parameter.
    * @param array unitsPaths - array of units path definition. Each nasted parameter from the top of single data object needs to be included as separate array element. Required units should be passed in separate array or object.
    * @return array - array with converted units for defined parameters (also stored in processedData when no data attribute is passed). Each unit will be replaced with following object: { raw: rawValue, value: convertedValue, type: unitType }
    */
	dateProcessing(data, datePaths) {
		let j, customDataSet = true, processed = [];
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.processedData);
			customDataSet = false;
		}
		if (typeof (datePaths) == 'undefined') {
			datePaths = this.datePaths;
		}
		processed = data;
		for (j in datePaths) {
			processed = this.objectProcessingRecursive(processed, datePaths[j], 0, this.dateProcessingRecursive);
		}
		if (customDataSet) {
			return processed;
		}
		this.processedData = processed;
		return this.processedData;
	}
    /**
    * method used for recursive processing of object
    * @param object object - data object which will be processed
    * @param array destinationPath - array of destination parameters path definition. Each nasted parameter from the top of single data object needs to be included as separate array element. Required parameters should be passed in separate array or object.
    * @param int depth - optional (default 0), defines current depth of processing
    * @param function callback - callback which will be called when destination parameter will be reached. Callback will be executed with 2 parameters: parameterValue and parameterName
    * @return object - object with processed destination parameters
    */
	objectProcessingRecursive(object, destinationPath, depth, callback) {
		let output = {}, curKey, i, j, curPath;
		if (typeof (callback) !== 'function') {
			throw new TypeError('objectProcessingRecursive: passed callback is not a function');
		}
		output = object;
		if (!(depth >= destinationPath.length || typeof (object) !== 'object' || object === null)) {
			//regular nasted processing
			curKey = destinationPath[depth];
			if (depth == (destinationPath.length - 1) && typeof (curKey) !== 'object') {
				//no next level
				curKey = [curKey];
			}
			if (typeof (curKey) === 'object' && curKey.length) {
				//we should look for units nodes
				for (j in curKey) {
					curPath = curKey[j];
					if (object.hasOwnProperty(curPath) && (typeof (object[curPath]) !== 'object' || typeof (object[curPath]) !== 'function')) {
						output[curPath] = callback(object[curPath], curPath, object);
					}
				}
			}
			else if (curKey && object.hasOwnProperty(curKey)) {
				output[curKey] = this.objectProcessingRecursive(object[curKey], destinationPath, depth + 1, callback);
			}
			else if ((depth + 1) < destinationPath.length && typeof (curKey) === 'object' && curKey.hasOwnProperty('length') && curKey.length == 0) {
				for (i in object) {
					if (typeof (object[i]) === 'object' && object[i] !== null) {
						output[i] = this.objectProcessingRecursive(object[i], destinationPath, depth + 1, callback);
					}
				}
			}
		}
		return output;
	}
    /**
    * callback used for recursive enums processing
    * @param string parameterValue - enum key
    * @param string parameterName - parameter name, will be mapped via enumsMapping - see description on the class definition
    * @return object - with following keys: raw - copy of parameterValue input, value - enumerated (more keys can be returned, depends on enumDef declaration in helpers)
    */
	enumsProcessingRecursive(parameterValue, parameterName, parent) {
		if (!(this.enumValueProvider instanceof Object) || !(this.enumValueProvider.enumerate instanceof Function)) {
			throw new Error("Trying to process enums without proper enumValueProvider injected");
		}
		let output = {}, enumMap;
		if (typeof (parameterValue) == 'object' && parameterValue !== null) {
			return parameterValue;
		}
		if (this.enumsMapping.hasOwnProperty(parameterName)) {
			enumMap = this.enumsMapping[parameterName];
			if (typeof (enumMap) == 'object') {
				output = this.enumValueProvider.enumerate(parameterValue, enumMap.enumDef, true, enumMap.specificPropertyOnly, enumMap.defaultKey);
			}
			else if (this.enumDefs.hasOwnProperty(enumMap)) {
				output = this.enumValueProvider.enumerate(parameterValue, this.enumDefs[enumMap], true, undefined, undefined);
			}
		}
		else if (this.enumDefs.hasOwnProperty(parameterName)) {
			output = this.helpers.enumerate(parameterValue, this.enumDefs[parameterName], true, undefined, undefined);
		}
		return output;
	}
    /**
    * callback used for recursive date values processing
    * @param string parameterValue - date to parse (UTC is default)
    * @param string parameterName - name of parameter
    * @return object - with following keys: raw - copy of parameterValue input, ts - timestamp
    */
	dateProcessingRecursive(parameterValue, parameterName, parent) {
		let output = {};
		if (typeof (parameterValue) == 'object' && parameterValue !== null) {
			return parameterValue;
		}
		output.raw = parameterValue;
		try {
			if (Date.hasOwnProperty('_parse')) {
				output.ts = Date._parse(parameterValue);
				if (isNaN(output.ts)) {
					//issues with safari
					output.ts = Date.parse(parameterValue);
				}
			}
			else {
				output.ts = Date.parse(parameterValue);
			}
			if (typeof (output.ts) == 'object') {
				output.ts = output.ts.getTime();
			}
			if (output.ts) {
				//include timezone offset
				output.ts -= new Date().getTimezoneOffset() * 60 * 1000;
			}
		}
		catch (e) {
			output.ts = 0;
		}
		return output;
	}
    /**
    * callback used for recursive units processing
    * @param string parameterValue - SI value
    * @param string parameterName - name of parameter which will determine type of measurement and further conversion. See helpers.baseballUnits for more details
    * @return object - with following keys: raw - copy of parameterValue input, value - converted value, type - determines the type of measurment, unit - unit fo current measurment
    */
	unitsProcessingRecursive(parameterValue, parameterName, parent) {
		let callback, unitDefinition = this.helpers.getUnitDefinition(parameterName), output = { value: null, raw: null, type: null, unit: null }, tempVal;
		if (typeof (parameterValue) == 'object' && parameterValue !== null) {
			return parameterValue;
		}
		output.raw = parameterValue;
		if (unitDefinition) {
			output.type = unitDefinition.type;
			if (unitDefinition.type == 'posXYZ') {
				if (parameterValue) {
					tempVal = parameterValue.split(';');
				}
				if (!tempVal || tempVal.length == 1) {
					tempVal = [];
				}
				output.x = (isNaN(tempVal[0]) ? null : parseFloat(tempVal[0]));
				output.y = (isNaN(tempVal[1]) ? null : parseFloat(tempVal[1]));
				output.z = (isNaN(tempVal[2]) ? null : parseFloat(tempVal[2]));
			}
			else {
				//value, type, precision, abbr, sysFrom, localize, sysTo)
				if (output.raw !== null) {
					output.value = this.converter.convertType(parameterValue, unitDefinition.type, unitDefinition.round, false);
				}
				if ((this.converter.sys == 'imperial' && unitDefinition.type == 'short-distance-ftin') || output.value == '-') {
					output.unit = '';
				}
				else if (this.converter.sys == 'imperial' && unitDefinition.type == 'short-distance') {
					output.unit = '′';
				}
				else {
					output.unit = this.converter.getUnit(unitDefinition.type);
				}
			}
		}
		callback = 'unitsCallback_' + parameterName;
		if (typeof this[callback] == 'function') {
			output = this[callback](output, parameterValue, parameterName, parent);
		}
		return output;
	}
    /**
    * data grouping, results are stored in data attribute
    * @param array data - optional (default this.sortedData), array of parameters or parameter which will be used during aggregation as a key
    * @param array groupBy - optional (default this.groupBy), array of keys for grouping. Each array element will be used in separate, recursive interation. If single element will be defined as an array, each key from array will be used as unique identificator
	* @param array groupBase - optional (default this.groupBase), arrray of keys for base value (from which iteration will start to map the collection)
	* @param boolean allowUndefined - optional (default true), determines how items with undefined value used for grouping will be handled, when set to false - error will be thrown, when set to true, mapUndefinedTo value will be used as grouping key
	* @param string mapUndefinedTo - optional (default 'undefined'), key used for grouping of items which has no path value available
    * @return object - collection of grouped items, property will be set grouping key, value will be an array with items
    */
	groupData(data, groupBy, groupBase, allowUndefined, mapUndefinedTo) {
		let g, groupingCallback, customDataSet = true, baseData;
		if (typeof (data) === 'undefined') {
			if (!this.sortedData) {
				throw new Error('groupData: sortedData attribute is empty');
			}
			data = this.cloneObject(this.sortedData);
			customDataSet = false;
		}
		if (typeof (groupBy) === 'undefined') {
			groupBy = this.groupBy;
		}
		if (typeof (groupBase) === 'undefined') {
			groupBase = this.groupBase;
		}
		if (typeof (allowUndefined) === 'undefined') {
			allowUndefined = true;
		}
		if (typeof (allowUndefined) === 'undefined' && allowUndefined) {
			mapUndefinedTo = 'undefined';
		}

		baseData = this.getObjectContent(data, groupBase);
		groupingCallback = function (parameterValue, parameterName, parent) {
			return baseData;
		};
		if (groupBy.length) {
			for (g = 0; g < groupBy.length; g++) {
				baseData = this.groupDataRecursively(groupBy[g], baseData, g, undefined, allowUndefined, mapUndefinedTo);
			}
			if (groupBase.length) {
				//here come the magic - replace only the filter base
				data = this.objectProcessingRecursive(data, groupBase, 0, groupingCallback);
			}
			else {
				data = baseData;
			}
		}
		if (customDataSet) {
			return data;
		}
		this.data = data;
		return this.data;
	}
    /**
    * recoursive data (sortedData) grouping
    * @param array / string grouping - array of parameters or parameter which will be used during aggregation as a key
    * @param object data - optional (default filteredData) collection of items that will be grouped
    * @param integer depth - optional (default 0), how deep iteration should process
    * @param integer curDepth - optional (default 0), how deep current iteration is
	* @param boolean allowUndefined - optional (default true), determines how items with undefined value used for grouping will be handled, when set to false - error will be thrown, when set to true, mapUndefinedTo value will be used as grouping key
	* @param string mapUndefinedTo - optional (default 'undefined'), key used for grouping of items which has no path value available
    * @return object - collection of grouped items, property will be set grouping key, value will be an array with items
    */
	groupDataRecursively(grouping, data, depth, curDepth, allowUndefined, mapUndefinedTo) {
		if (typeof (data) === 'undefined') {
			data = this.cloneObject(this.sortedData);
		}
		if (typeof (depth) === 'undefined') {
			depth = 0;
		}
		if (typeof (curDepth) === 'undefined') {
			curDepth = 0;
		}
		if (typeof (allowUndefined) === 'undefined') {
			allowUndefined = true;
		}
		if (typeof (allowUndefined) === 'undefined' && allowUndefined) {
			mapUndefinedTo = 'undefined';
		}

		let curItem, groupingVal, i, j, groupingKey, grouped = {}, groupingCallback = false;
		if (typeof (grouping) === 'string') {
			grouping = [grouping];
		}
		groupingKey = grouping.join('_');
		if (typeof this['grouping_' + groupingKey] === 'function') {
			groupingCallback = 'grouping_' + groupingKey;
		}
		for (i in data) {
			curItem = data[i];
			groupingVal = '';
			if (curDepth == depth) {
				if (groupingCallback) {
					groupingVal = this[groupingCallback](curItem);
				}
				else {
					let objectData = this.getObjectContent(curItem, grouping);
					if (!allowUndefined && typeof (objectData) === 'undefined') {
						throw new Error('groupDataRecursively: Cannot get object value using path ['+(grouping.join(','))+'] @ index ' + i);
					}
					if (typeof (objectData) === 'function' && !allowUndefined) {
						throw new Error('groupDataRecursively: Cannot stringify function @ index ' + i);
					}
					if (typeof (objectData) === 'object') {
						if (objectData.hasOwnProperty('raw') || allowUndefined) {
							if (groupingVal) {
								groupingVal += '-';
							}
							if (!objectData || typeof(objectData['raw']) === 'undefined') {
								if (!allowUndefined) {
									throw new Error('groupDataRecursively: Cannot get raw object value using path ['+(grouping.join(','))+'] @ index ' + i);
								}
								groupingVal += mapUndefinedTo;
							}
							else {
								groupingVal += objectData['raw'];
							}
						}
						else {
							throw new Error('groupDataRecursively: Cannot stringify object using path ['+(grouping.join(','))+'] @ index ' + i);
						}
					}
					else {
						if (groupingVal) {
							groupingVal += '-';
						}
						if (typeof(objectData) === 'undefined') {
							if (!allowUndefined) {
								throw new Error('groupDataRecursively: Cannot get raw object value using path ['+(grouping.join(','))+'] @ index ' + i);
							}
							groupingVal += mapUndefinedTo;
						}
						else {
							groupingVal += objectData;
						}
					}
				}
				if (groupingVal) {
					if (!grouped.hasOwnProperty(groupingVal)) {
						grouped[groupingVal] = [];
					}
					grouped[groupingVal].push(curItem);
				}
			}
			else {
				grouped[i] = this.groupDataRecursively(grouping, curItem, depth, curDepth + 1, allowUndefined);
			}
		}
		return grouped;
	}
    /**
    * reProcess current rawData. It will execute filterData, sortData, unitsProcessing and groupData
    * @return object - reprocessed data, stored also in data attribute
    */
	reProcess() {
		if (!this.rawData) {
			throw new Error('No rawData, nothing to process!');
		}
		this.filterData();
		this.calcStats();
		this.sortData();
		this.groupData();
		this.postProcess();
		return this.data;
	}
    /**
    * reProcess current rawData with additional preProecss, units, dates and enums conversion. It will also execute filterData, sortData, unitsProcessing and groupData
    * @return object - reprocessed data, stored also in data attribute
    */
	reProcessRaw() {
		if (!this.rawData) {
			throw new Error('No rawData, nothing to process!');
		}
		this.preProcess();
		this.enumsProcessing();
		this.dateProcessing();
		this.unitsProcessing();
		return this.reProcess();
	}

	/**
    * get unique values from data set
    * @param object data - required, collection of items that will be grouped
    * @param array properties - required, array with properties which will be used to determine uniqueness
    * @param bool includeCount - optional (default true), determines the output type, if true object with uniqueKey: count will be returned, array with uniqueKeys otherwise
    * @return object / array - collection of unique items (output type depends on includeCount)
    */
   static getUniqueValues(data, properties, includeCount) {
		const unique = {}, uniqueArray = [];

		if (typeof (data) !== 'object') {
			throw new TypeError('data must be an array or object');
		}
		// backward compatibility...
		if (typeof (properties) === 'string') {
			properties = [properties];
		}
		if (typeof (properties) !== 'object') {
			throw new TypeError('properties must be an array or object')
		}
		if (typeof (includeCount) == 'undefined') {
			includeCount = true;
		}

		for (let i in data) {
			const curItem = data[i];
			let uniqueKey;

			if (!properties.length) {
				uniqueKey = curItem;
			}
			else {
				for (let j in properties) {
					const property = properties[j];
					if (curItem.hasOwnProperty(property)) {
						if (typeof (curItem[property]) === 'object' || typeof (curItem[property]) === 'function') {
							throw new Error('getUniqueValues: Cannot stringify object or function: ' + property + '@ index ' + i);
						}
						if (uniqueKey) {
							uniqueKey += '-' + curItem[property];
						}
						else {
							uniqueKey = curItem[property];
						}
					}
				}
			}
			if (includeCount) {
				if (unique.hasOwnProperty(uniqueKey)) {
					unique[uniqueKey]++;
				}
				else {
					unique[uniqueKey] = 1;
					uniqueArray.push(uniqueKey);
				}
			}
			else if (uniqueArray.indexOf(uniqueKey) === -1) {
				uniqueArray.push(uniqueKey);
			}
		}

		return includeCount ? unique: uniqueArray;
	}

    /**
    * get unique values from data set
    * @param object data - optional (default filteredData), collection of items that will be grouped
    * @param array properties - optional (default uniqueProperties), array with properties which will be used to determine uniqueness
    * @param bool includeCount - optional (default true), determines the output type, if true object with uniqueKey: count will be returned, array with uniqueKeys otherwise
    * @return object / array - collection of unique items (output type depends on includeCount)
    */
	getUniqueValues(data, properties, includeCount) {
		var uniqueVal, i, j, property, unique = {}, uniqueArray = [], uniqueCallback = false;
		if (typeof (includeCount) == 'undefined') {
			includeCount = true;
		}
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.filteredData);
		}
		if (typeof (properties) === 'undefined') {
			properties = this.uniqueProperties;
		}
		if (typeof (properties) === 'string') {
			properties = [properties];
		}
		uniqueVal = properties.join('_');
		if (typeof this['unique_' + uniqueVal] == 'function') {
			uniqueCallback = this['unique_' + uniqueVal];
		}
		for (i in data) {
			let curItem = data[i];
			let uniqueKey;
			if (uniqueCallback) {
				unique = uniqueCallback(unique, curItem, properties);
			}
			else {
				if (!properties.length) {
					uniqueKey = curItem;
				}
				else {
					for (j in properties) {
						property = properties[j];
						if (curItem.hasOwnProperty(property)) {
							if (typeof (curItem[property]) === 'object' || typeof (curItem[property]) === 'function') {
								throw new Error('getUniqueValues: Cannot stringify object or function: ' + property + '@ index ' + i);
							}
							if (uniqueKey) {
								uniqueKey += '-' + curItem[property];
							}
							else {
								uniqueKey = curItem[property];
							}
						}
					}
				}
				if (includeCount) {
					if (unique.hasOwnProperty(uniqueKey)) {
						unique[uniqueKey]++;
					}
					else {
						unique[uniqueKey] = 1;
						uniqueArray.push(uniqueKey);
					}
				}
				else if (uniqueArray.indexOf(uniqueKey) === -1) {
					uniqueArray.push(uniqueKey);
				}
			}
		}
		if (includeCount) {
			return unique;
		}
		else {
			return uniqueArray;
		}
	}
    /**
    * get unique values recursively from data set
    * @param bool includeCount - optional (default true), determines the output type, if true object with uniqueKey: count will be returned, array with uniqueKeys otherwise
    * @param object data - optional (default filteredData), collection of items that will be used as data source
    * @param array uniquePropertiesPath - optional (default uniquePropertiesPath), array with properties destination path which will be used to determine uniqueness
    * @return object / array - collection of unique items (output type depends on includeCount)
    */
	getUniqueValuesRecursive(includeCount, data, uniquePropertiesPath) {
		var i, unique = {}, uniqueArray = [], callback;
		if (typeof (includeCount) == 'undefined') {
			includeCount = true;
		}
		if (typeof (data) == 'undefined') {
			data = this.cloneObject(this.filteredData);
		}
		if (typeof (uniquePropertiesPath) == 'undefined') {
			uniquePropertiesPath = this.uniquePropertiesPath;
		}
		if (typeof (uniquePropertiesPath) == 'string') {
			uniquePropertiesPath = [uniquePropertiesPath];
		}
		callback = function (parameterValue, parameterName, parent) {
			if (unique.hasOwnProperty(parameterValue)) {
				unique[parameterValue]++;
			}
			else {
				unique[parameterValue] = 1;
				uniqueArray.push(parameterValue);
			}
			return parameterValue;
		};
		for (i in uniquePropertiesPath) {
			this.objectProcessingRecursive(data, uniquePropertiesPath[i], 0, callback);
		}
		if (includeCount) {
			return unique;
		}
		else {
			return uniqueArray;
		}
	}

    /**
    * calculates statistical data for specified data set
    * @param array type - optional (default all stats from StatType Enum), array of strings, determines types of statistical data to calculate
    * @param object data - optional (default filteredData), collection of items that will be used as data source
    * @param array statsDataPaths - optional (default statsDataPaths), array with properties destination path which will be used to determine population
    * @param bool unitsProcessing - optional (default true), should units processing be executed before returning output
    * @return object - parameter name as an key and object with statistical data associated with it. Each type of stats data will be assigned to parameter with name of it. If unitsProcessing is included raw value and converted value wrapped in object will be attached instead of single value
    */
	calcStats(type, data, statsDataPaths, unitsProcessing) {
		// TODO - make it static...
		var i, j, k, l, m, dataSet = {}, stats = {}, sum, range,
			dev, min, max, avg, cv, count, ratio, ratioCount,
			q1, q2, q3, qcd, iqr,
			singleVal, ratioTmp, parameterValues, parameterNames = [], customDataSet = true, callback,
			//Largest Remainder Method temporary table
			LRM = [], LRMsum;
		const allTypes = [
			StatType.Avg.key, StatType.Min.key, StatType.Max.key, StatType.StDev.key, StatType.Count.key, StatType.Sum.key, StatType.Range.key,
			StatType.Ratio.key, StatType.CV.key, StatType.Q1.key, StatType.Q2.key, StatType.Q3.key, StatType.QCD.key, StatType.IQR.key
		];
		if (typeof (type) === 'undefined') {
			type = allTypes;
		}
		else {
			if (!(type instanceof Array)) {
				throw new TypeError('type must be an array of strings');
			}
			const unknownTypes = type.filter(x => !allTypes.includes(x));
			if (unknownTypes.length) {
				throw new Error('unknown types of stats provided (please referer to StatType Enum): '.unknownTypes.join(', '));
			}
		}

		if (typeof (data) === 'undefined') {
			data = this.cloneObject(this.filteredData);
			customDataSet = false;
		}
		if (typeof (statsDataPaths) === 'undefined') {
			statsDataPaths = this.statsDataPaths;
		}
		if (typeof (statsDataPaths) === 'string') {
			statsDataPaths = [statsDataPaths];
		}
		const hasRecursiveData = statsDataPaths && statsDataPaths.length > 0;

		if (typeof (unitsProcessing) === 'undefined') {
			unitsProcessing = true;
		}
		callback = function (parameterValue, parameterName, parent) {
			let value;
			if (!dataSet.hasOwnProperty(parameterName)) {
				dataSet[parameterName] = [];
			}
			if (typeof (parameterValue) === 'object' && parameterValue !== null) {
				if (parameterValue.hasOwnProperty('raw')) {
					value = parameterValue.raw;
				}
				else {
					value = this.getUniqueValues(parameterValue, ['raw'], false);
				}
			}
			else {
				value = parameterValue;
			}
			dataSet[parameterName].push((isNaN(value) || value === null) ? value : parseFloat(value));
			return parameterValue;
		}.bind(this);

		if (!hasRecursiveData) {
			data.forEach(element => {
				callback(element, 'undefined');
			});
		}
		else {
			for (i in statsDataPaths) {
				this.objectProcessingRecursive(data, statsDataPaths[i], 0, callback);
			}
		}

		for (j in dataSet) {
			parameterValues = dataSet[j];
			parameterNames.push(j);
			min = null;
			max = null;
			dev = null;
			avg = null;
			cv = null;
			q1 = null;
			q2 = null;
			q3 = null;
			qcd = null;
			iqr = null;
			count = 0;
			ratio = {};
			ratioCount = 0;
			LRMsum = 0;
			sum = 0;
			range = null;
			LRM = [];

			if (typeof (parameterValues) !== 'undefined' && parameterValues.length) {
				var sortedNumbers = parameterValues
					.filter(n => !Number.isNaN(n) && typeof(n) !== 'undefined' && n !== null)
					.sort((a, b) => a - b);

				count = sortedNumbers.length;
				if (count) {
					min = sortedNumbers[0];
					max = sortedNumbers[count-1];
					range = max - min;
				}

				for (k = 0; k < parameterValues.length; k++) {
					if (isNaN(parameterValues[k])) {
						if (type.indexOf(StatType.Ratio.key) > -1 && parameterValues[k] && parameterValues[k].length) {
							ratioCount++;
							if (typeof (parameterValues[k]) === 'string') {
								//calculate the ratio
								if (ratio.hasOwnProperty(parameterValues[k])) {
									ratio[parameterValues[k]]++;
								}
								else {
									ratio[parameterValues[k]] = 1;
								}
							}
							else if (parameterValues[k] instanceof Array) {
								//additional support for multiple values for single data set item
								for (m = 0; m < parameterValues[k].length; m++) {
									singleVal = parameterValues[k][m];
									//calculate the ratio
									if (ratio.hasOwnProperty(singleVal)) {
										ratio[singleVal]++;
									}
									else {
										ratio[singleVal] = 1;
									}
								}
							}
						}
						//parameterValues[k] = parseFloat(parameterValues[k]);
					}
					else if (!Number.isNaN(parameterValues[k]) && typeof(parameterValues[k]) !== 'undefined' && parameterValues[k] !== null) {
						sum += parameterValues[k];
					}
				}

				if (count) {
					avg = sum / count;
				}

				if (count > 2) {
					({ q1, q2, q3 } = StatisticalHelper.DetermineQuartiles(sortedNumbers));
					iqr = q3 - q1;
					qcd = iqr / (q3 + q1);
				}

				if (count > 0 && (type.indexOf(StatType.StDev.key) > -1 || type.indexOf(StatType.CV.key) > -1)) { //maybe count > 1 ? not sure how to interpret it
					for (k = 0; k < sortedNumbers.length; k++) {
						dev += Math.pow(sortedNumbers[k] - avg, 2);
					}

					dev = Math.sqrt(dev / count);
					cv = dev/avg;
				}

				if (type.indexOf(StatType.Ratio.key) > -1) {
					for (k in ratio) {
						if (!ratio.hasOwnProperty(k)){
							continue;
						}
						ratio[k] = {
							ratio: (ratio[k] * 100 / ratioCount),
							count: ratio[k],
							initial: ratio[k]
						};
						if (k != 'all') {
							LRM[k] = ratio[k].ratio;
							LRMsum += ratio[k].ratio;
						}
					}
					//fit rounded values to the sum
					LRM = this.largestRemainderMethodRound(LRM, Math.round(LRMsum));
					//replace
					for (k in LRM) {
						ratio[k].percent = LRM[k];
						if (unitsProcessing) {
							ratioTmp = this.unitsProcessingRecursive(ratio[k].ratio, 'statsRatio');
							ratioTmp.count = ratio[k].count;
							ratio[k] = ratioTmp;
						}
					}
				}
			}

			stats[j] = {};
			if (type.indexOf(StatType.StDev.key) > -1) {
				stats[j][StatType.StDev.key] = dev;
			}
			if (type.indexOf(StatType.Avg.key) > -1) {
				stats[j][StatType.Avg.key] = avg;
			}
			if (type.indexOf(StatType.Min.key) > -1) {
				stats[j][StatType.Min.key] = min;
			}
			if (type.indexOf(StatType.Max.key) > -1) {
				stats[j][StatType.Max.key] = max;
			}
			if (type.indexOf(StatType.Count.key) > -1) {
				stats[j][StatType.Count.key] = count;
			}
			if (type.indexOf(StatType.Sum.key) > -1) {
				stats[j][StatType.Sum.key] = sum;
			}
			if (type.indexOf(StatType.Range.key) > -1) {
				stats[j][StatType.Range.key] = range;
			}
			if (type.indexOf(StatType.CV.key) > -1) {
				stats[j][StatType.CV.key] = cv;
			}
			if (type.indexOf(StatType.Q1.key) > -1) {
				stats[j][StatType.Q1.key] = q1;
			}
			if (type.indexOf(StatType.Q2.key) > -1) {
				stats[j][StatType.Q2.key] = q2;
			}
			if (type.indexOf(StatType.Q3.key) > -1) {
				stats[j][StatType.Q3.key] = q3;
			}
			if (type.indexOf(StatType.QCD.key) > -1) {
				stats[j][StatType.QCD.key] = qcd;
			}
			if (type.indexOf(StatType.IQR.key) > -1) {
				stats[j][StatType.IQR.key] = iqr;
			}
			if (unitsProcessing) {
				for (l in stats[j]) {
					stats[j][l] = this.unitsProcessingRecursive(stats[j][l], j);
				}
			}
			//put also ratio data
			if (ratioCount && type.indexOf(StatType.Ratio.key) > -1) {
				stats[j][StatType.Ratio.key] = ratio || {};
			}
			//units processing is not necessary for count
			if (type.indexOf(StatType.Count.key) > -1) {
				stats[j][StatType.Ratio.key] = count;
			}
		}
		if (!customDataSet) {
			if (this.filteredData instanceof Object && statsDataPaths.length) {
				this.filteredData.calculatedStats = stats;
			}
			this.calculatedStats = stats;
		}

		return hasRecursiveData ? stats: stats['undefined'];
	}
    /**
    * round set of data to specific value, usually 100%
    * @param object items - array/object of floats to round
    * @param int upTo - optional (default sum of all input items), goal
    * @return object - collection of values after rounding
    */
	largestRemainderMethodRound(items, upTo) {
		let remainders = [], i, curItem;
		let sumAfter = 0;

		if (typeof (upTo) == 'undefined') {
			upTo = 0;
			for (let j in items) {
				upTo += items[j];
			}
		}

		if (upTo && items) {
			for (i in items) {
				curItem = {};
				curItem.raw = items[i];
				curItem.int = Math.floor(items[i]);
				curItem.rem = items[i] - curItem.int;
				curItem.key = i;
				items[i] = curItem.int;
				remainders.push(curItem);
				sumAfter += curItem.int;
			}

			//sort the remainders
			remainders.sort(function (a, b) {
				return (a.rem - b.rem) * -1;
			});

			i = 0;
			while (sumAfter < upTo && i < remainders.length) {
				curItem = remainders[i];
				items[curItem.key]++;
				i++;
				sumAfter++;
			}
		}
		return items;
	}

    /**
    * get the map with specific parameters from collection
    * @param object input - array/object of items
    * @param array parameterPath - path of destination parameter to extract from input elements
    * @param bool reverseMap - should the map be returned in reverse order ?
    * @return object - collection with key and parameter map
    */
	getObjectParameterMap(input, parameterPath, reverseMap) {
		let i, map = {};
		reverseMap = reverseMap || false;
		for (i in input) {
			if (reverseMap) {
				map[this.getObjectContent(input[i], parameterPath)] = i;
			}
			else {
				map[i] = this.getObjectContent(input[i], parameterPath);
			}
		}
		return map;
	}

	mapCollectionProperties(input, parameterPaths) {
		let map = [];

		for (const i in input) {
			let mapped = {};
			for (let l = 0; l < parameterPaths.length; l++){
				let inpObj = input[i];
				let parameterPath = parameterPaths[l];
				if (typeof(parameterPath) !== 'object'){
					parameterPath = [parameterPath];
				}

				//console.log(`${ inpObj.TagID} => ${this.getObjectContent( inpObj, parameterPath)}`);
				mapped[parameterPath[parameterPath.length - 1]] = this.getObjectContent(inpObj, parameterPath);
				//mapped[parameterPath[parameterPath.length - 1]] = inpObj.TagID;
			}

			map.push(mapped);
		}

		return map;
	}
}

export default IterableDataProcessor;
